import React from "react";
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  getDVIRQuestions,
  toggleShowAddQuestionModal,
  toggleShowEditQuestionModal,
  setSelectedQuestion,
  showDeleteQuestionConfirmationModal,
} from "../../dvirSlice";
import { DVIRQuestion, DVIRQuestionType } from "../../../../models";
import "./DVIRQuestionsTab.scss";

const getQuestionTypeColor = (type: DVIRQuestionType): string => {
  switch (type) {
    case "boolean":
      return "type-boolean";
    case "text":
      return "type-text";
    case "number":
      return "type-number";
    default:
      return "type-boolean";
  }
};

const getQuestionTypeLabel = (type: DVIRQuestionType): string => {
  switch (type) {
    case "boolean":
      return "Boolean";
    case "text":
      return "Text";
    case "number":
      return "Number";
    default:
      return "Boolean";
  }
};

export function DVIRQuestionsTab() {
  const dispatch = useAppDispatch();

  const questions = useAppSelector((state) => state.dvir.questions);
  const getDVIRQuestionsAPI = useAppSelector(
    (state) => state.dvir.getDVIRQuestions
  );
  const deleteDVIRQuestionAPI = useAppSelector(
    (state) => state.dvir.deleteDVIRQuestion
  );

  React.useEffect(() => {
    dispatch(getDVIRQuestions());
  }, [dispatch]);

  React.useEffect(() => {
    // Refetch questions after successful delete
    if (!deleteDVIRQuestionAPI.loading && deleteDVIRQuestionAPI.value) {
      dispatch(getDVIRQuestions());
    }
  }, [deleteDVIRQuestionAPI.loading, deleteDVIRQuestionAPI.value, dispatch]);

  const handleAddQuestion = () => {
    dispatch(toggleShowAddQuestionModal());
  };

  const handleEditQuestion = (question: DVIRQuestion) => {
    dispatch(setSelectedQuestion(question));
    dispatch(toggleShowEditQuestionModal());
  };

  const handleDeleteQuestion = (question: DVIRQuestion) => {
    dispatch(showDeleteQuestionConfirmationModal(question));
  };

  if (getDVIRQuestionsAPI.loading) {
    return (
      <div className="dvir-questions-tab loading">
        <div className="loading-text">Loading questions...</div>
      </div>
    );
  }

  return (
    <div className="dvir-questions-tab">
      <div className="header">
        <div className="title-section">
          <h2>DVIR Questions</h2>
          <p className="subtitle">
            Manage questions that can be used in DVIR templates
          </p>
        </div>
        <button className="btn btn-primary" onClick={handleAddQuestion}>
          + Add New Question
        </button>
      </div>

      {questions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-message">
            <h3>No questions found</h3>
            <p>Get started by creating your first DVIR question.</p>
            <button className="btn btn-primary" onClick={handleAddQuestion}>
              Create First Question
            </button>
          </div>
        </div>
      ) : (
        <div className="questions-table-container">
          <div className="table-header">
            <h3>All Questions ({questions.length})</h3>
          </div>
          <div className="table-scroll-wrapper">
            <table className="questions-table">
              <thead>
                <tr>
                  <th>Question Text</th>
                  <th>Type</th>
                  <th>Used in Templates</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {questions.map((question: DVIRQuestion) => (
                  <tr key={question.id}>
                    <td className="question-text-cell">
                      <div className="question-text">{question.questionText}</div>
                    </td>
                    <td>
                      <span
                        className={`question-type-badge ${getQuestionTypeColor(
                          question.questionType
                        )}`}
                      >
                        {getQuestionTypeLabel(question.questionType)}
                      </span>
                    </td>
                    <td className="usage-cell">
                      <span className="usage-count">
                        {question.templateCount}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEditQuestion(question)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteQuestion(question)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
